/**
 * Convert height to feet and inches for display.
 * Accepts: "5'7"", "5'7", "67 in", "170 cm", "170cm", etc.
 * Returns "X'Y"" or original string if unparseable.
 */
export function formatHeight(str) {
  if (!str || typeof str !== 'string') return str || '';
  const s = str.trim();
  if (!s) return '';

  // Already feet and inches: 5'7", 5'7, 5' 10"
  const ftInMatch = s.match(/^(\d+)\s*'\s*(\d+)\s*"?$/i);
  if (ftInMatch) {
    const ft = parseInt(ftInMatch[1], 10);
    const inch = parseInt(ftInMatch[2], 10);
    return `${ft}'${inch}"`;
  }

  // Just feet: 5'
  const ftOnlyMatch = s.match(/^(\d+)\s*'?\s*$/i);
  if (ftOnlyMatch) return `${ftOnlyMatch[1]}'`;

  // Inches: "67 in", "67 inches" (require unit so we don't treat "170" as inches)
  const inMatch = s.match(/^(\d+(?:\.\d+)?)\s*(?:in|inches?)$/i);
  if (inMatch) {
    const totalInches = parseFloat(inMatch[1], 10);
    const ft = Math.floor(totalInches / 12);
    const inch = Math.round(totalInches % 12);
    return inch > 0 ? `${ft}'${inch}"` : `${ft}'`;
  }

  // Centimeters: "170 cm", "170cm"
  const cmMatch = s.match(/^(\d+(?:\.\d+)?)\s*cm$/i);
  if (cmMatch) {
    const cm = parseFloat(cmMatch[1], 10);
    const totalInches = cm / 2.54;
    const ft = Math.floor(totalInches / 12);
    const inch = Math.round(totalInches % 12);
    return inch > 0 ? `${ft}'${inch}"` : `${ft}'`;
  }

  return s;
}

/**
 * Convert weight to lbs for display.
 * Accepts: "95 kg", "95kg", "210 lbs", "210", etc.
 * Returns "X lbs" or original string if unparseable.
 */
export function formatWeight(str) {
  if (!str || typeof str !== 'string') return str || '';
  const s = str.trim();
  if (!s) return '';

  // Kilograms: "95 kg", "95kg"
  const kgMatch = s.match(/^(\d+(?:\.\d+)?)\s*kg$/i);
  if (kgMatch) {
    const kg = parseFloat(kgMatch[1], 10);
    const lbs = Math.round(kg * 2.20462);
    return `${lbs} lbs`;
  }

  // Already lbs: "210 lbs", "210 lb"
  const lbsMatch = s.match(/^(\d+(?:\.\d+)?)\s*(?:lbs?|pounds?)$/i);
  if (lbsMatch) {
    const n = parseFloat(lbsMatch[1], 10);
    return Number.isFinite(n) ? `${Math.round(n)} lbs` : s;
  }

  return s;
}
